// Enhanced Error Reporting Service with API proxy for Pub/Sub
class ErrorReportingService {
  private static instance: ErrorReportingService;
  private apiBaseUrl: string;
  private pubsubEndpoint: string;
  private environment: string;
  private userId: string | undefined;

  private constructor() {
    this.apiBaseUrl = process.env.REACT_APP_MILL_TESTS_API_URL || '';
    this.environment = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
    this.userId = process.env.REACT_APP_SLACK_USER_ID;
    
    // Construct the pub/sub endpoint
    this.pubsubEndpoint = this.apiBaseUrl.startsWith('http') 
      ? `${this.apiBaseUrl}/error/api/publish`
      : `http://${this.apiBaseUrl}/error/api/publish`;

    this.setupGlobalErrorHandlers();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 ErrorReporting initialized for environment:', this.environment);
      console.log('📡 API Base URL:', this.apiBaseUrl);
      console.log('🔗 Pub/Sub Endpoint:', this.pubsubEndpoint);
      console.log('👤 User ID for pinging:', this.userId ? 'Configured ✅' : 'Not configured ❌');
    }
  }

  public static getInstance(): ErrorReportingService {
    if (!ErrorReportingService.instance) {
      ErrorReportingService.instance = new ErrorReportingService();
    }
    return ErrorReportingService.instance;
  }

  private setupGlobalErrorHandlers() {
    // Handle unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      this.sendErrorToPubSub({
        type: 'JavaScript Error',
        message: event.error?.message || event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.sendErrorToPubSub({
        type: 'Unhandled Promise Rejection',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
    });
  }

  public async sendErrorToPubSub(errorData: {
    type: string;
    message: string;
    filename?: string;
    lineno?: number;
    colno?: number;
    stack?: string;
    url: string;
    userAgent: string;
    timestamp: string;
    customData?: any;
  }) {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Debug - ErrorReportingService sending error to Pub/Sub:', errorData.type);
      console.log('🔍 Debug - Pub/Sub endpoint:', this.pubsubEndpoint);
    }
    
    if (!this.apiBaseUrl) {
      console.warn('API base URL not configured. Set REACT_APP_MILL_TESTS_API_URL environment variable.');
      return;
    }

    // Create the message payload for Pub/Sub
    const messagePayload = {
      // Error details
      errorType: errorData.type,
      message: errorData.message,
      stack: errorData.stack,
      filename: errorData.filename,
      lineno: errorData.lineno,
      colno: errorData.colno,
      
      // Context information
      environment: this.environment,
      timestamp: errorData.timestamp,
      url: errorData.url,
      userAgent: errorData.userAgent,
      userId: this.userId,
      
      // Application context
      appName: 'mill-tests-webapp',
      source: 'frontend',
      
      // Custom data if provided
      customData: errorData.customData
    };

    try {
      const response = await fetch(this.pubsubEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messagePayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Pub/Sub proxy failed: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Error sent to Pub/Sub via API proxy:', result);
      }
    } catch (error) {
      console.error('❌ Failed to send error to Pub/Sub:', error);
      
      // Fallback: log the error data to console for debugging
      console.error('Error data that failed to send:', messagePayload);
    }
  }

  // Method to manually report custom errors
  public reportCustomError(message: string, customData?: any) {
    this.sendErrorToPubSub({
      type: 'Custom Error',
      message,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      customData
    });
  }

  // Method to report API errors
  public reportApiError(endpoint: string, status: number, statusText: string, responseData?: any) {
    this.sendErrorToPubSub({
      type: 'API Error',
      message: `API call failed: ${endpoint}`,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      customData: {
        endpoint,
        status,
        statusText,
        responseData
      }
    });
  }

  // Method to get current environment info
  public getEnvironmentInfo(): { environment: string; apiConfigured: boolean } {
    return {
      environment: this.environment,
      apiConfigured: !!this.apiBaseUrl
    };
  }
}

export default ErrorReportingService;
